// src/components/common/PeriodPicker.tsx
//
// Shared date-range control for the Dirección analytics pages (Resumen,
// Tiempos, Cobros, Hoteles): a `from`/`to` pair plus «Comparar con el periodo
// anterior» (Decision-12 comparison columns). Defaults to the last 30 days,
// today included, on the browser's local calendar.
import React from 'react';
import { Box, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { useTranslate } from 'react-admin';

export interface PeriodValue {
  from: string;
  to: string;
  compare: boolean;
}

/** `yyyy-MM-dd` of a date on the browser's local calendar (never UTC: after local midnight
 *  the UTC date is still yesterday). */
export const localIsoDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** The last `days` days, today included: today−(days−1) … today, local dates. */
export const defaultPeriod = (days = 30, now: Date = new Date()): PeriodValue => {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  return { from: localIsoDate(from), to: localIsoDate(now), compare: true };
};

interface PeriodPickerProps {
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  showCompare?: boolean;
}

export const PeriodPicker: React.FC<PeriodPickerProps> = ({ value, onChange, showCompare = true }) => {
  const translate = useTranslate();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
      <TextField
        type="date"
        size="small"
        label={translate('common.period.from')}
        value={value.from}
        onChange={(e) => onChange({ ...value, from: e.target.value })}
        InputLabelProps={{ shrink: true }}
        sx={{ width: 152 }}
      />
      <TextField
        type="date"
        size="small"
        label={translate('common.period.to')}
        value={value.to}
        onChange={(e) => onChange({ ...value, to: e.target.value })}
        InputLabelProps={{ shrink: true }}
        sx={{ width: 152 }}
      />
      {showCompare && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={value.compare}
              onChange={(e) => onChange({ ...value, compare: e.target.checked })}
            />
          }
          label={translate('common.period.compare')}
        />
      )}
    </Box>
  );
};
